// Data
import FULL_PRODUCT_DATA from "../../static/data/full-product"
import FULL_APPAREL_DATA from "../../static/data/full-apparel"

/**
 * @param id - product id
 * @returns string - the corresponding icon for the current product type
 * */
export const handleProductIconById = id => {
  let icon = ""

  if (FULL_PRODUCT_DATA.hasOwnProperty(id)) {
    icon =
      (FULL_PRODUCT_DATA[id].type.toLowerCase() === "workout plan" && "flex") ||
      (FULL_PRODUCT_DATA[id].type.toLowerCase() === "meal plan" && "utensils") ||
      (FULL_PRODUCT_DATA[id].type.toLowerCase() === "supplements" && "leaf") ||
      (FULL_PRODUCT_DATA[id].type.toLowerCase() === "bundles" && "leaf")
  }

  if (FULL_APPAREL_DATA.hasOwnProperty(id)) {
    icon =
      (FULL_APPAREL_DATA[id].type.toLowerCase() === "apparel" && "shirt") ||
      (FULL_APPAREL_DATA[id].type.toLowerCase() === "merchandise" && "tag")
  }

  return icon
}

/**
 * @param type - product type
 * @returns string - the corresponding icon for the current product type
 * */
export const handleProductIconByType = type => {
  return (
    (type.toLowerCase() === "workout plan" && "flex") ||
    (type.toLowerCase() === "meal plan" && "utensils") ||
    (type.toLowerCase() === "supplements" && "leaf") ||
    (type.toLowerCase() === "bundles" && "leaf") ||
    (type.toLowerCase() === "apparel" && "tshirt") ||
    (type.toLowerCase() === "merchandise" && "tag")
  )
}

/**
 * @param tab - product title
 * @returns string - removes dashes and replaces with empty places and adds heart if vanilla or chocolate protein
 * */
export const formatTabs = tab => {
  // if the tab is a vanilla or chocolate protein
  let formattedTab = tab.replaceAll("-", " ")

  if (formattedTab === "vanilla protein" || formattedTab === "chocolate protein") {
    formattedTab = `I <i class="fa-solid fa-heart txt--pink"></i> ${formattedTab}`
  }

  return formattedTab
}

/**
 * @param title - product title
 * @param shopifyProduct - the current shopify product
 * @returns string - replace heart with font awesome icon or returns original title
 * */
export const formatProductTitle = (title, shopifyProduct) => {
  // If the title contains heart, replace the heart with fontawesome heart
  if (title.indexOf("❤") !== -1) {
    title = title.replace("❤", '<i class="fa-solid fa-heart txt--pink"></i>')
  }

  // If the title is workout bundle, add color to + free protein
  if (title.indexOf("Workout Bundle") !== -1) {
    const titleParts = title.split("(")
    title = `${titleParts[0]} <span class="product__title--sm txt--pink">(${titleParts[1]}</span>`
  }

  // Remove Aminos from Rock and Restore title
  if (title.toLowerCase().includes("rock and restore aminos")) {
    title = title.toLowerCase().replace("rock and restore aminos", "rock and restore")
  }

  // If the title contains buy 3 get one free, add color to it
  if (title.toLowerCase().includes("buy 3 get one free")) {
    title = title
      .toLowerCase()
      .replace("(buy 3 get one free)", '<span class="product__title--sm txt--pink">(Buy 3 Get One Free)</span>')
  }

  // if the title contains buy 3 get 1 free, add color to it
  if (title.toLowerCase().includes("buy 3 get 1 free")) {
    title = title
      .toLowerCase()
      .replace("(buy 3 get 1 free)", '<span class="product__title--sm txt--pink">(Buy 3 Get One Free)</span>')
  }

  // if the title contains protein bundle
  if (shopifyProduct.tags.indexOf("promo") !== -1 && title.toLowerCase().includes("protein bundle")) {
    title = title
      .toLowerCase()
      .replace("protein bundle", '<span class="d-inline-block txt--capitalize txt--pink">Protein Bundle</span>')
    title = `<span class="txt--uppercase">${title}</span>`
  }

  return title
}

/**
 * @returns string - remove trademark in title
 * */
export const removeTM = title => {
  return title.replace("™", "")
}

/**
 * @param shopifyProduct - the data of the product
 * @param cartItem - the data from the localStorage
 * @param productData - the static data from the product
 * @returns string - formatted product description
 * */
export const formatProductDescriptionCartMessage = (cartItem, shopifyProduct, productData) => {
  if (!productData) return

  let message = ""

  const months = productData.sellingPlanMonths ? productData.sellingPlanMonths : 3

  if (shopifyProduct.sellingPlans.length && cartItem.selling_plan !== "No") {
    shopifyProduct.sellingPlans.forEach(plan => {
      if (Number(plan.id) === Number(cartItem.selling_plan)) {
        if (cartItem.type === "supplements" || cartItem.type === "bundles") {
          message = `Subscribed - Delivery every ${plan.intervalCount} ${plan.interval.toLowerCase()}s`
        } else {
          message = `<strong>Payment Plan:</strong> ${months} ${plan.interval.toLowerCase()}s x ${formatPrice(plan.price)}`
        }
      }
    })
  } else {
    // If there are options, check if any is selected, and use the name of it for the message
    shopifyProduct.variants.forEach(variant => {
      if (cartItem.variantId === variant.shopifyId && variant.selectedOptions.length > 0) {
        message = variant.selectedOptions[0].value

        // Add a second selected option if there is one
        if (variant.selectedOptions[1]) message += ` / ${variant.selectedOptions[1].value}`
      }
    })

    if (message.toLowerCase() === "default title") message = "One Type Payment Plan"
  }

  return message
}

/**
 *  @param cartItem - the data from the localStorage
 *  @param shopifyProduct - the data from the shopify product
 *  @param productData - the static data from the shopify product
 *  @returns string - formatted product description
 * */
export const formatProductDescriptionSidebarMessage = (cartItem, shopifyProduct, productData) => {
  if (!productData) return

  let message = ""

  const months = productData.sellingPlanMonths ? productData.sellingPlanMonths : 3
  const additionalMonths = months - 1

  if (shopifyProduct.sellingPlans.length && cartItem.selling_plan !== "No") {
    shopifyProduct.sellingPlans.forEach(plan => {
      if (Number(plan.id) === Number(cartItem.selling_plan)) {
        if (cartItem.type === "supplements" || cartItem.type === "bundles") {
          message = `Subscribed - Delivery every ${plan.intervalCount} ${plan.interval.toLowerCase()}${plan.intervalCount > 1 ? "s" : ""}`
        } else {
          message = `You will be charged ${formatPrice(plan.price)} today, and an additional  ${formatPrice(plan.price)} next ${additionalMonths > 1 ? `${additionalMonths} months` : "month"}`
        }
      }
    })
  } else {
    // If there are options, check if any is selected, and use the name of it for the message
    shopifyProduct.variants.forEach(variant => {
      if (cartItem.variantId === variant.shopifyId && variant.selectedOptions.length > 0) {
        message = variant.selectedOptions[0].value

        // Add a second selected option if there is one
        if (variant.selectedOptions[1]) message += ` / ${variant.selectedOptions[1].value}`
      }
    })

    if (message.toLowerCase() === "default title") message = ""
  }

  return message
}

/**
 *  @param amount - value to be formatted
 *  @param decimals - number of decimals
 *  @returns string - formatted price
 * */
export const formatPrice = (amount, decimals = 0) => {
  // Convert to Number just in case it's a string
  // The rounding up of the price is added specifically for the product 90DCH since on recharge the correct price cannot be set
  const num = amount.toString().endsWith(".99") ? Math.round(amount) : Number(amount)

  // Format with USD currency, but keep all decimals
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: decimals, // always at least 2 decimals
    maximumFractionDigits: decimals, // allow more if needed
  }).format(num)
}
